'use client';

import { motion } from 'framer-motion';
import React from 'react';

import ContactActionButtons from '@/components/contact-actions';
import SectionHeading from '../layout/section-heading';

const BOOKS = [
    {
        labelTop: 'Book',
        spineTitle: 'Essays',
        color: '#B75A90',
        height: 210,
        width: 66,
        tilt: -7,
        x: 0,
        y: 0,
        z: 1,
        textColor: '#FFFFFF',
    },
    {
        spineTitle: "I Don't Want To Die Poor:",
        color: '#C06496',
        height: 235,
        width: 72,
        tilt: -2,
        x: 58,
        y: 0,
        z: 3,
        textColor: '#FFFFFF',
    },
    {
        spineTitle: 'Lifting as we climb',
        color: '#F2B400',
        height: 245,
        width: 56,
        tilt: 5,
        x: 130,
        y: 0,
        z: 2,
        textColor: '#1A1A1A',
    },
    {
        labelTop: 'Book',
        spineTitle: 'Life Events',
        subLabel: 'Book\n2020',
        color: '#E6422B',
        height: 225,
        width: 70,
        tilt: 9,
        x: 184,
        y: 0,
        z: 4,
        textColor: '#FFFFFF',
    },
    {
        labelTop: 'Book',
        spineTitle: 'Look',
        subLabel: '2024',
        color: '#1F56D8',
        height: 165,
        width: 38,
        tilt: 6,
        x: 260,
        y: 0,
        z: 5,
        textColor: '#FFFFFF',
    },
    {
        labelTop: 'Book',
        spineTitle: 'In The Dream House',
        color: '#F4A5C8',
        height: 240,
        width: 56,
        tilt: -5,
        x: 292,
        y: 0,
        z: 6,
        textColor: '#FFFFFF',
    },
];

function UmlautDots() {
    return (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex gap-4">
            <span className="block h-7 w-7 rounded-full" style={{ backgroundColor: '#74C7FF' }} />
            <span className="block h-7 w-7 rounded-full" style={{ backgroundColor: '#F4A5C8' }} />
        </div>
    );
}

function BookSpine({ book, index }) {
    const enterDelay = 0.28 + index * 0.12;

    return (
        <motion.div
            className="absolute bottom-0 rounded-[4px] shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
            style={{
                height: book.height,
                width: book.width,
                backgroundColor: book.color,
                zIndex: book.z,
                transformOrigin: 'bottom center',
            }}
            // ✅ Position + tilt are handled by Framer (not style.transform)
            initial={{
                opacity: 0,
                y: 90,
                x: book.x,
                rotate: book.tilt - 6,
            }}
            animate={{
                opacity: 1,
                y: book.y ?? 0,
                x: book.x,
                rotate: book.tilt,
            }}
            transition={{
                delay: enterDelay,
                type: 'spring',
                stiffness: 140,
                damping: 16,
            }}
            whileHover={{
                y: (book.y ?? 0) - 12,
                rotate: 0,
                scale: 1.06,
                zIndex: 50,
            }}
        >
            {/* Subtle idle animation */}
            <motion.div
                className="h-full w-full"
                animate={{
                    y: [0, -3, 0],
                    rotate: [0, book.tilt > 0 ? 1.2 : -1.2, 0],
                }}
                transition={{
                    duration: 4.2 + index * 0.2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.8 + index * 0.15,
                }}
            >
                {/* Top label */}
                {book.labelTop ? (
                    <div
                        className="absolute left-1/2 top-2 -translate-x-1/2 text-[11px] tracking-wide opacity-90"
                        style={{ color: book.textColor ?? '#fff' }}
                    >
                        {book.labelTop}
                    </div>
                ) : null}

                {/* Optional small sub label box */}
                {book.subLabel ? (
                    <div
                        className="absolute left-1/2 top-10 -translate-x-1/2 rounded-sm border px-2 py-1 text-[10px] leading-tight opacity-95"
                        style={{
                            color: book.textColor ?? '#fff',
                            borderColor: (book.textColor ?? '#fff') + 'AA',
                        }}
                    >
                        {book.subLabel.split('\n').map((line, i) => (
                            <div key={i} className="text-center">
                                {line}
                            </div>
                        ))}
                    </div>
                ) : null}

                {/* Vertical title */}
                <div
                    className="absolute inset-0 flex items-center justify-center px-2 text-center font-semibold tracking-wide"
                    style={{
                        color: book.textColor ?? '#fff',
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                    }}
                >
                    <span className="text-[18px] leading-none">{book.spineTitle}</span>
                </div>
            </motion.div>
        </motion.div>
    );
}

export default function AnimatedBookWordmark() {
    return (
        <section className="w-full overflow-hidden bg-linear-to-b bg-neutral-800">
            <div className="mx-auto max-w-6xl px-4">
                <SectionHeading
                    titleClassName="mx-auto mb-6 text-white"
                    descriptionClassName="max-w-6xl text-white/80"
                    align="center"
                    title="Your Story Deserves to Be Published"
                    description="We offer a complete set of professional publishing services that turn your manuscript into a book you can proudly present to readers everywhere. From expert editing and eye catching cover design to global distribution and focused marketing, we handle the essential work so you can share your story with strength and confidence."
                />
                <div className="relative flex items-end justify-center max-md:hidden">
                    <div className="relative flex items-end justify-center select-none">
                        {/* B */}
                        <motion.div
                            className="text-white font-black leading-none"
                            style={{
                                fontSize: 'clamp(84px, 12vw, 190px)',
                                letterSpacing: '0.01em',
                            }}
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: 'easeOut' }}
                        >
                            B
                        </motion.div>

                        {/* First O + dots */}
                        <div className="relative mx-2">
                            <motion.div
                                className="text-white font-black leading-none"
                                style={{
                                    fontSize: 'clamp(84px, 12vw, 190px)',
                                    letterSpacing: '0.01em',
                                }}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.55, ease: 'easeOut', delay: 0.05 }}
                            >
                                O
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.9 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
                            >
                                <UmlautDots />
                            </motion.div>
                        </div>

                        {/* Books between O's */}
                        <div
                            className="relative mx-1"
                            style={{
                                width: 360,
                                height: 270,
                            }}
                        >
                            {/* floor shadow */}
                            <div className="absolute bottom-0 left-1/2 h-6 w-[94%] -translate-x-1/2 rounded-full bg-black/40 blur-[10px]" />

                            {BOOKS.map((b, i) => (
                                <BookSpine key={`${b.spineTitle}-${i}`} book={b} index={i} />
                            ))}
                        </div>

                        {/* Second O */}
                        <motion.div
                            className="text-white font-black leading-none mx-2"
                            style={{
                                fontSize: 'clamp(84px, 12vw, 190px)',
                                letterSpacing: '0.01em',
                            }}
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: 'easeOut', delay: 0.1 }}
                        >
                            O
                        </motion.div>

                        {/* K */}
                        <motion.div
                            className="text-white font-black leading-none"
                            style={{
                                fontSize: 'clamp(84px, 12vw, 190px)',
                                letterSpacing: '0.01em',
                            }}
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease: 'easeOut', delay: 0.15 }}
                        >
                            K
                        </motion.div>
                    </div>
                </div>
            </div>
            <div className="mx-auto max-w-6xl flex justify-center items-center mt-18 container">
                <ContactActionButtons
                    show={['chat', 'call']}
                    onChat={() => window?.Tawk_API?.maximize?.()} // example for tawk
                    phone="+15408938895"
                    chatVariant="secondary"
                    callVariant="outline"
                    size="3xl"
                />
            </div>
        </section>
    );
}
